// src/pages/NoticeWarningPage.jsx
import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API_BASE } from "../api/config";
import "./NoticeWarningPage.css";

const emptyNotice = {
  enabled: true,
  title: "",
  body: "",
  warning: "",
};

export default function NoticeWarningPage() {
  const [promotionPolicy, setPromotionPolicy] = useState(emptyNotice);
  const [promotionRule, setPromotionRule] = useState(emptyNotice);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [message, setMessage] = useState("");

  const loadNotices = async () => {
    setLoading(true);
    setMessage("");

    try {
      const res = await fetch(`${API_BASE}/api/pos-notices`);
      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json?.success) {
        setMessage(json?.message || "❌ Notice data load failed");
        return;
      }

      const notices = json.notices || {};

      setPromotionPolicy(notices.promotionPolicy || emptyNotice);
      setPromotionRule(notices.promotionRule || emptyNotice);
    } catch (err) {
      console.error("Load notice error:", err);
      setMessage("❌ Notice data load error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotices();
  }, []);

  const updatePolicyField = (field, value) => {
    setPromotionPolicy((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateRuleField = (field, value) => {
    setPromotionRule((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage("");

    try {
      const payload = {
        promotionPolicy,
        promotionRule,
      };

      const res = await fetch(`${API_BASE}/api/pos-notices`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json?.success) {
        setMessage(json?.message || "❌ Save failed");
        return;
      }

      setMessage("✅ POS notice / warning text updated successfully");
      await loadNotices();
    } catch (err) {
      console.error("Save notice error:", err);
      setMessage("❌ Save error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title="POS Notice / Warning Settings">
      <div className="notice-page">
        <div className="notice-header">
          <div>
            <h2>POS Notice / Warning Settings</h2>
            <p>
              ဒီနေရာမှာစာပြင်လိုက်တာနဲ့ POS မှာပြထားတဲ့ notice box စာသားတွေ
              real-time ပြောင်းသွားမယ်။
            </p>
          </div>

          <button
            type="button"
            className="notice-refresh-btn"
            onClick={loadNotices}
            disabled={loading}
          >
            Refresh
          </button>
        </div>

        {message && <div className="notice-message">{message}</div>}

        {loading ? (
          <div className="notice-loading">Loading notice settings...</div>
        ) : (
          <>
            <div className="notice-grid">
              <div className="notice-card">
                <div className="notice-card-title">
                  ⚠ Promotion / Gift Voucher Warning Box
                </div>

                <label className="notice-check">
                  <input
                    type="checkbox"
                    checked={promotionPolicy.enabled === true}
                    onChange={(e) =>
                      updatePolicyField("enabled", e.target.checked)
                    }
                  />
                  Show this box on POS
                </label>

                <div className="notice-form-group">
                  <label>Title</label>
                  <input
                    value={promotionPolicy.title || ""}
                    onChange={(e) =>
                      updatePolicyField("title", e.target.value)
                    }
                    placeholder="Notice title"
                  />
                </div>

                <div className="notice-form-group">
                  <label>Body Text</label>
                  <textarea
                    rows="6"
                    value={promotionPolicy.body || ""}
                    onChange={(e) => updatePolicyField("body", e.target.value)}
                    placeholder="Main warning text"
                  />
                </div>

                <div className="notice-form-group">
                  <label>Warning Highlight Text</label>
                  <textarea
                    rows="3"
                    value={promotionPolicy.warning || ""}
                    onChange={(e) =>
                      updatePolicyField("warning", e.target.value)
                    }
                    placeholder="Important warning text"
                  />
                </div>
              </div>

              <div className="notice-card">
                <div className="notice-card-title">📢 Promotion Rule Box</div>

                <label className="notice-check">
                  <input
                    type="checkbox"
                    checked={promotionRule.enabled === true}
                    onChange={(e) =>
                      updateRuleField("enabled", e.target.checked)
                    }
                  />
                  Show this box on POS
                </label>

                <div className="notice-form-group">
                  <label>Title</label>
                  <input
                    value={promotionRule.title || ""}
                    onChange={(e) => updateRuleField("title", e.target.value)}
                    placeholder="Promotion rule title"
                  />
                </div>

                <div className="notice-form-group">
                  <label>Body Text</label>
                  <textarea
                    rows="6"
                    value={promotionRule.body || ""}
                    onChange={(e) => updateRuleField("body", e.target.value)}
                    placeholder="Promotion rule description"
                  />
                </div>

                <div className="notice-form-group">
                  <label>Warning / Note Text</label>
                  <textarea
                    rows="3"
                    value={promotionRule.warning || ""}
                    onChange={(e) =>
                      updateRuleField("warning", e.target.value)
                    }
                    placeholder="Promotion rule important note"
                  />
                </div>
              </div>
            </div>

            <div className="notice-actions">
              <button
                type="button"
                className="notice-save-btn"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Notice Text"}
              </button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}